export interface ILinePayload {
  company_number: string;
  address: string;
  name: string;
  country_code: string;
  phone_type: PhoneType;
  phone_service: PhoneService;
  customer_proof_type: CustomerProofType;
  ein: string;
  customer_proof_website: string;
  file?: File | string;
}

export type LineAgentRef = string | { _id?: string } | null | undefined;

export interface IActiveCampaignLock {
  _id: string;
  campaign_name: string;
  status: string;
  channel?: string;
}

export interface ILineResponse extends ILinePayload {
  _id: string;
  status: LineStatus;
  createdAt: string;
  line_number: string;
  attached_inbound_agent?: LineAgentRef;
  attached_sms_agent?: LineAgentRef;
  attached_whatsapp_agent?: LineAgentRef;
  call_enabled?: boolean;
  sms_enabled?: boolean;
  whatsapp_enabled?: boolean;
  active_campaigns?: IActiveCampaignLock[];
}

export enum PhoneType {
  LOCAL = 1,
  TOLL_FREE = 2,
}

export enum PhoneService {
  CALLS_ONLY = 1,
  CALLS_SMS = 2,
}

export enum CustomerProofType {
  WEBSITE_URL = 1,
  UPLOAD_IMG = 2,
}

export enum LineStatus {
  ACTIVE = 1,
  PAUSED = 2,
  MESSAGE_SERVICE_PENDING = 3,
}

export interface IGetLines {
  limit: number;
  page: number;
  line_number: string;
  status?: LineStatus[];
  sort?: Record<string, 1 | -1>;
}

export interface IUpdateLine {
  status?: LineStatus;
  call_enabled?: boolean;
  sms_enabled?: boolean;
  whatsapp_enabled?: boolean;
}

export type PhoneChannelKey = "call" | "sms" | "whatsapp";

export const lineAgentId = (value?: LineAgentRef): string => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  return String(value._id || "").trim();
};

/** SMS agent is its own field. Legacy lines fall back to the call agent until split. */
export const smsAgentId = (line?: ILineResponse | null): string => {
  if (!line) return "";
  if (line.attached_sms_agent !== undefined) {
    return lineAgentId(line.attached_sms_agent);
  }
  if (line.sms_enabled === false) return "";
  return lineAgentId(line.attached_inbound_agent);
};

const coerceFlag = (flag: unknown): boolean | undefined => {
  if (flag === true || flag === "true" || flag === 1 || flag === "1") return true;
  if (flag === false || flag === "false" || flag === 0 || flag === "0") return false;
  return undefined;
};

const flagOrLegacy = (flag: unknown, legacyOn: boolean): boolean => {
  const coerced = coerceFlag(flag);
  if (coerced === true) return true;
  if (coerced === false) return false;
  return legacyOn;
};

/** Call toggle. Agent assignment is independent — turn the channel on first. */
export const isCallChannelOn = (line?: ILineResponse | null): boolean => {
  if (!line) return false;
  return flagOrLegacy(line.call_enabled, line.status === LineStatus.ACTIVE);
};

export const isCallEnabled = isCallChannelOn;

/** SMS toggle. Agent assignment is independent — turn the channel on first. */
export const isSmsChannelOn = (line?: ILineResponse | null): boolean => {
  if (!line || line.phone_service !== PhoneService.CALLS_SMS) return false;
  return flagOrLegacy(line.sms_enabled, true);
};

/** True when this number has an inbound SMS agent assigned. */
export const isSmsEnabled = (line?: ILineResponse | null): boolean => {
  return isSmsChannelOn(line) && Boolean(smsAgentId(line));
};

/** WhatsApp toggle. Agent assignment is independent — turn the channel on first. */
export const isWhatsAppEnabled = (line?: ILineResponse | null): boolean => {
  if (!line) return false;
  return flagOrLegacy(
    line.whatsapp_enabled,
    Boolean(lineAgentId(line.attached_whatsapp_agent))
  );
};

export enum LineInboundType {
  CALL = 1,
  WHATSAPP = 2,
  SMS = 3,
}

/** Assigned inbound agent for this channel (Phone Settings dropdown). */
export const inboundAgentIdForChannel = (
  line?: ILineResponse | null,
  channel?: string
): string => {
  if (!line || !channel) return "";
  if (channel === "sms") return smsAgentId(line);
  if (channel === "whatsapp") return lineAgentId(line.attached_whatsapp_agent);
  if (channel === "call") return lineAgentId(line.attached_inbound_agent);
  return "";
};

/**
 * A number is never inbound-only. Enabled SMS/WhatsApp can still be a
 * campaign "from" number; inbound replies use the assigned inbound agent.
 */
export const isReservedForMessagingCampaign = (
  _line?: ILineResponse | null,
  _channel?: string
): boolean => false;

/**
 * Campaign "from" numbers: channel Enabled. Inbound replies use the
 * assigned inbound agent, not the campaign agent.
 */
export const allowsCampaignAutoReply = (
  line?: ILineResponse | null,
  channel?: string
): boolean => {
  if (!line || !channel) return false;
  if (channel === "sms") return isSmsChannelOn(line);
  if (channel === "whatsapp") return isWhatsAppEnabled(line);
  return false;
};

/**
 * Channels this number can use for campaigns.
 * Call is always allowed. SMS/WhatsApp require the channel Enabled.
 */
export const getEnabledPhoneChannels = (
  line?: ILineResponse | null
): PhoneChannelKey[] => {
  if (!line) return [];
  const channels: PhoneChannelKey[] = ["call"];
  if (isSmsChannelOn(line)) channels.push("sms");
  if (isWhatsAppEnabled(line)) channels.push("whatsapp");
  return channels;
};
